import { HydratorResolver } from "@its-engineering-and-development/rbac-react";
import { store } from "@/store";
import { setPermissions } from "@/store/permissions.slice";

/**
 * Access manifest returned by the BFF `myExpenseAccess` query. The BFF is the
 * single policy decision point: it expands coarse role actions into granular
 * per-module actions and decides `canAccessApp`, so the client no longer owns
 * that mapping.
 */
interface AccessManifest {
    canAccessApp: boolean;
    allowedActions: string[];
    modules: Array<{ key: string; canView: boolean; canEdit: boolean }>;
}

const MY_EXPENSE_ACCESS_QUERY = `query MyExpenseAccess {
  myExpenseAccess {
    canAccessApp
    allowedActions
    modules { key canView canEdit }
  }
}`;

// ============================================================================
// localStorage cache
// ============================================================================

const PERMISSIONS_STORAGE_KEY = "itseng_permissions_cache";
const MAX_CACHE_AGE_MS = 14 * 60 * 1000; // 14 minutes
const PERMISSIONS_REQUEST_TIMEOUT_MS = 10_000;

interface StoredPermissions {
    sub: string;
    fingerprint: string;
    allowedActions: string[];
    canAccessApp: boolean;
    cachedAt: number;
}

function getTokenSub(token: string): string {
    try {
        const raw = token.split(".")[1];
        if (!raw) return "";
        // JWT uses base64url; normalize to standard base64 before decoding.
        const base64 = raw
            .replace(/-/g, "+")
            .replace(/_/g, "/")
            .padEnd(Math.ceil(raw.length / 4) * 4, "=");
        const payload = JSON.parse(atob(base64)) as Record<string, unknown>;
        return typeof payload.sub === "string" ? payload.sub : "";
    } catch {
        return "";
    }
}

function readStoredPermissions(token: string): StoredPermissions | null {
    try {
        const raw = localStorage.getItem(PERMISSIONS_STORAGE_KEY);
        if (!raw) return null;
        const p = JSON.parse(raw) as Record<string, unknown>;
        if (
            typeof p.sub !== "string" ||
            typeof p.fingerprint !== "string" ||
            typeof p.cachedAt !== "number" ||
            typeof p.canAccessApp !== "boolean" ||
            !Array.isArray(p.allowedActions) ||
            !(p.allowedActions as unknown[]).every((a) => typeof a === "string")
        ) {
            return null;
        }
        if (Date.now() - p.cachedAt > MAX_CACHE_AGE_MS) return null;
        // Reject a cache entry belonging to a different user.
        if (p.sub !== getTokenSub(token)) return null;
        return {
            sub: p.sub as string,
            fingerprint: p.fingerprint as string,
            allowedActions: p.allowedActions as string[],
            canAccessApp: p.canAccessApp as boolean,
            cachedAt: p.cachedAt as number,
        };
    } catch {
        return null;
    }
}

function writeStoredPermissions(data: StoredPermissions): void {
    try {
        localStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify(data));
    } catch {
        // quota or security policy error — non-fatal
    }
}

function clearStoredPermissions(): void {
    try {
        localStorage.removeItem(PERMISSIONS_STORAGE_KEY);
    } catch {
        // ignore
    }
}

// Module-level deduplication cache — keyed on the sorted action fingerprint.
let _lastFingerprint: string | null = null;
let _lastResult: string[] | null = null;
let _lastValidatedAt: number | null = null;
let _cacheGeneration = 0;

/** Stable fingerprint for sorted action lists; order-independent string comparison. */
function fingerprintActions(actions: string[]): string {
    return JSON.stringify([...actions].sort());
}

async function fetchAndApplyPermissions(token: string, requestGeneration: number): Promise<string[]> {
    const endpoint = (import.meta.env.VITE_GRAPHQL_ENDPOINT ?? "").trim();
    if (!endpoint) {
        throw new Error("VITE_GRAPHQL_ENDPOINT is not configured");
    }
    const apiKey = (import.meta.env.VITE_GRAPHQL_API_KEY ?? "").trim();

    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
            controller.abort();
            reject(new Error("Loading permissions timed out after 10 seconds"));
        }, PERMISSIONS_REQUEST_TIMEOUT_MS);
    });

    const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
    };
    if (apiKey) {
        headers["x-api-key"] = apiKey;
    }

    let response: Response;
    try {
        response = await Promise.race([
            fetch(endpoint, {
                method: "POST",
                cache: "no-store",
                headers,
                body: JSON.stringify({ query: MY_EXPENSE_ACCESS_QUERY }),
                signal: controller.signal,
            }),
            timeout,
        ]);
    } finally {
        clearTimeout(timeoutId);
    }

    if (!response.ok) {
        throw new Error(`Failed to load permissions (${response.status})`);
    }

    const payload = (await response.json()) as {
        data?: { myExpenseAccess?: AccessManifest };
        errors?: Array<{ message?: string }>;
    };
    if (requestGeneration !== _cacheGeneration) {
        return _lastResult ?? [];
    }

    if (payload.errors?.length) {
        throw new Error(payload.errors[0]?.message ?? "Failed to load permissions");
    }

    const manifest = payload.data?.myExpenseAccess;

    // The BFF returns the already-expanded granular actions and the access
    // decision; a user with zero access resolves to an empty set so the app can
    // detect "no access" and log them out.
    const resolved = Array.isArray(manifest?.allowedActions) ? manifest.allowedActions.filter((a): a is string => typeof a === "string") : [];
    const canAccessApp = manifest?.canAccessApp === true;
    const fingerprint = fingerprintActions(resolved);
    const validatedAt = Date.now();

    _lastValidatedAt = validatedAt;

    if (fingerprint === _lastFingerprint && _lastResult !== null) {
        writeStoredPermissions({ sub: getTokenSub(token), fingerprint, allowedActions: _lastResult, canAccessApp, cachedAt: validatedAt });
        return _lastResult;
    }

    _lastFingerprint = fingerprint;
    _lastResult = resolved;
    store.dispatch(setPermissions({ allowedActions: resolved, roles: [], canAccessApp }));
    writeStoredPermissions({ sub: getTokenSub(token), fingerprint, allowedActions: resolved, canAccessApp, cachedAt: validatedAt });

    return resolved;
}

async function hydrateAllowedActions(token: string): Promise<string[]> {
    const requestGeneration = _cacheGeneration;

    // Stale-while-revalidate on page reload: seed synchronously from a valid
    // localStorage entry so the shell renders without a flash, then revalidate
    // against the network in the background so admin-side permission changes
    // (including full revocation → auto-logout) propagate on the same load
    // instead of waiting for the next background refresh.
    if (_lastResult === null) {
        const stored = readStoredPermissions(token);
        if (stored !== null) {
            _lastFingerprint = stored.fingerprint;
            _lastResult = stored.allowedActions;
            _lastValidatedAt = stored.cachedAt;
            store.dispatch(setPermissions({ allowedActions: stored.allowedActions, roles: [], canAccessApp: stored.canAccessApp }));
            void fetchAndApplyPermissions(token, requestGeneration).catch(() => {
                // Non-fatal: the seeded cache stays in effect until the next successful load.
                console.warn("Background permissions revalidation failed");
            });
            return stored.allowedActions;
        }
    }

    return fetchAndApplyPermissions(token, requestGeneration);
}

/** Reset the in-memory deduplication cache and the localStorage entry. Exposed for testing and logout cleanup. */
export function resetPermissionsCache(): void {
    _cacheGeneration += 1;
    _lastFingerprint = null;
    _lastResult = null;
    _lastValidatedAt = null;
    clearStoredPermissions();
}

export function getPermissionsLastValidatedAt(): number | null {
    return _lastValidatedAt;
}

// Invalidate pending responses whenever the active session token changes.
let _previousAccessToken = store.getState().auth.accessToken;
store.subscribe(() => {
    const accessToken = store.getState().auth.accessToken;
    if (accessToken !== _previousAccessToken) {
        resetPermissionsCache();
    }
    _previousAccessToken = accessToken;
});

export const expenseAdminPermissionResolver = new HydratorResolver(hydrateAllowedActions);
